import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { Modal } from 'components/common/Modal/Modal';
import { useCallAPI } from 'hooks/useCallAPI';
import i18n from 'i18n';
import _ from 'lodash';
import styles from './downloadReportPanel.module.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Tab, Tabs } from 'react-bootstrap';
import { DateRangePickerField } from 'components/common/form/field/DateRangePickerField';
import { Order } from 'core/order/Order';
import { SelectField } from 'components/common/form/field/SelectField';
import { CustomField } from 'components/common/form/field/CustomField';
import { DefaultReportManager } from 'core/report/ReportManager';
import { Fields, GroupBy } from './DownloadReportPanelModel';
import { L1Object } from 'core/l1Object/L1Object';
import { AddonFeatureManager } from 'core';
import { ADDONFEATURE } from 'core/agency/AddonFeature';
import {
  hasBeaconNetworkL1Object,
  hasOutdoorL1Object,
  OutdoorReportType
} from './outdoorReportTypes';
import {
  canDownloadOutdoorCustomReport,
  initialOutdoorFieldSelection,
  outdoorFieldErrors,
  outdoorQuickSelectSelection,
  OutdoorQuickSelect,
  OutdoorQuickSelectType,
  OUTDOOR_DATA_CONTENT_ROWS,
  OUTDOOR_VIDEO_FIELDS,
  selectedOutdoorMetrics
} from './outdoorCustomReportFields';
import classNames from 'classnames/bind';
const cx = classNames.bind(styles);

export enum OutdoorReportDownloadPanelTab {
  NORMAL = 'normal',
  CUSTOM_FIELDS = 'customFields'
}

/** 分組依據的選項順序 —— 依介面示意圖：廣告活動群組 / 廣告活動 / 素材。 */
const CUSTOM_FIELDS_GROUP_BY = [
  GroupBy.L1_OBJECT,
  GroupBy.CAMPAIGN,
  GroupBy.CREATIVE
];

type DownloadOutdoorReportPanelProps = {
  order: Order;
  /**
   * 訂單底下的 L1Object —— 「報表類型」的選項由這些活動的類型聯集決定。
   * 活動類型只存在於 L1Object，Order 型別本身沒有 channel / adType 欄位。
   */
  l1ObjectList: L1Object[];
  addonFeatureManager: AddonFeatureManager;
  onClose: () => void;
};

const reportManager = new DefaultReportManager();

export const DownloadOutdoorReportPanel: React.FC<
  DownloadOutdoorReportPanelProps
> = ({ order, l1ObjectList, addonFeatureManager, onClose }) => {
  const { loading, callAPIs } = useCallAPI();

  const [startDate, setStartDate] = useState(order.startDate);
  const [endDate, setEndDate] = useState(order.endDate);
  const [activeTab, setActiveTab] = useState<string>(
    OutdoorReportDownloadPanelTab.NORMAL
  );
  const [selectFields, setSelectFields] = useState(
    initialOutdoorFieldSelection
  );
  const [groupBy, setGroupBy] = useState<string>(GroupBy.CREATIVE);

  const handleDateRangeChange = useCallback(
    (start: string | undefined, end: string | undefined) => {
      start && setStartDate(start);
      end && setEndDate(end);
    },
    []
  );

  // 選項 = 該訂單含有的活動類型的聯集:
  //   一般 Outdoor → 戶外廣告 / 戶外機台廣告資訊 / 戶外機台廣告資訊(Line Simple Beacon)
  //   Beacon Network → Line Message
  //   兩者都有 → 上述 4 項
  const reportTypeOptions = useMemo(() => {
    const hasOutdoor = hasOutdoorL1Object(l1ObjectList);
    const hasBeaconNetwork = hasBeaconNetworkL1Object(l1ObjectList);
    return _.compact([
      hasOutdoor && {
        label: i18n.t('downloadReportPanel.labels.outdoorOption'),
        value: OutdoorReportType.OUTDOOR
      },
      hasOutdoor && {
        label: i18n.t('downloadReportPanel.labels.outdoorDeviceOption'),
        value: OutdoorReportType.OUTDOOR_DEVICE
      },
      hasOutdoor && {
        label: i18n.t('downloadReportPanel.labels.outdoorDeviceBeaconOption'),
        value: OutdoorReportType.OUTDOOR_DEVICE_BEACON
      },
      hasBeaconNetwork && {
        label: i18n.t('downloadReportPanel.labels.lineMessageOption'),
        value: OutdoorReportType.LINE_MESSAGE
      }
    ]);
  }, [l1ObjectList]);

  const [reportType, setReportType] = useState<string>(
    reportTypeOptions[0]?.value ?? ''
  );

  const onSelectReportType = useCallback((reportType: string) => {
    setReportType(reportType);
  }, []);

  // 選項清單變動後，選中值必須仍在清單內 —— 否則會停在一個已經消失的選項上，
  // 而 download() 沒有 else 分支，按下下載會靜默什麼都不做。
  useEffect(() => {
    if (!reportTypeOptions.some((option) => option.value === reportType)) {
      setReportType(reportTypeOptions[0]?.value ?? '');
    }
  }, [reportTypeOptions, reportType]);

  const download = useCallback(() => {
    if (reportType === OutdoorReportType.OUTDOOR) {
      const fields = [
        Fields.IMPRES,
        Fields.CPM,
        Fields.E_PUBLISHER_IMPRES,
        Fields.SPENT
      ];
      const { projectName, orderNumber } = order;
      callAPIs(
        [
          () =>
            reportManager.downloadOrderReport(
              {
                name: projectName,
                orderNumber
              },
              startDate,
              endDate,
              GroupBy.CREATIVE,
              fields
            )
        ],
        onClose
      );
    } else if (reportType === OutdoorReportType.OUTDOOR_DEVICE) {
      callAPIs(
        [
          () =>
            reportManager.downloadOrderOutdoorDeviceReport(
              order.orderNumber,
              startDate,
              endDate
            )
        ],
        onClose
      );
    } else if (reportType === OutdoorReportType.OUTDOOR_DEVICE_BEACON) {
      callAPIs(
        [
          () =>
            reportManager.downloadOrderOutdoorDeviceBeaconReport(
              order.orderNumber,
              startDate,
              endDate
            )
        ],
        onClose
      );
    } else if (reportType === OutdoorReportType.LINE_MESSAGE) {
      callAPIs(
        [
          () =>
            reportManager.downloadOrderBeaconEventReport(
              order.orderNumber,
              startDate,
              endDate
            )
        ],
        onClose
      );
    }
  }, [order, startDate, endDate, reportType, callAPIs, onClose]);

  const downloadCustomFieldsReport = useCallback(() => {
    const { projectName, orderNumber } = order;
    callAPIs(
      [
        () =>
          reportManager.downloadOrderReport(
            {
              name: projectName,
              orderNumber
            },
            startDate,
            endDate,
            groupBy,
            selectedOutdoorMetrics(selectFields)
          )
      ],
      onClose
    );
  }, [order, startDate, endDate, groupBy, selectFields, callAPIs, onClose]);

  const onFieldSelect = useCallback((event: any) => {
    const { id, checked } = event.target;
    setSelectFields((selected) => ({ ...selected, [id]: checked }));
  }, []);

  const onQuickSelect = useCallback((type: OutdoorQuickSelectType) => {
    setSelectFields(outdoorQuickSelectSelection(type));
  }, []);

  const changeGroupBy = useCallback((event: any) => {
    setGroupBy(event.target.value);
  }, []);

  const fieldErrors = useMemo(
    () => outdoorFieldErrors(selectFields),
    [selectFields]
  );

  const customFieldsEnabled = addonFeatureManager.isFeatureEnable(
    ADDONFEATURE.REPORT.REPORT_ORDER_CUSTOM
  );
  const isCustomFieldsTab =
    activeTab === OutdoorReportDownloadPanelTab.CUSTOM_FIELDS;

  const renderFieldChecks = (fields: Fields[]) => (
    <div className={styles.fieldChecks} key={fields.join()}>
      {fields.map((field) => (
        <Form.Check
          id={field}
          key={field}
          type='checkbox'
          className={cx({ error: field in fieldErrors })}
          checked={selectFields[field]}
          label={i18n.t(`downloadReportPanel.labels.${_.camelCase(field)}`)}
          onChange={onFieldSelect}
        />
      ))}
    </div>
  );

  const renderGroupBy = () =>
    CUSTOM_FIELDS_GROUP_BY.map((groupByType) => {
      const groupById = _.camelCase(`group_by_${groupByType.toLowerCase()}`);
      return (
        <Form.Check
          key={groupByType}
          type='radio'
          label={i18n.t(`downloadReportPanel.labels.${groupById}`)}
          value={groupByType}
          name='outdoorCustomFieldsGroupBy'
          id={groupById}
          checked={groupBy === groupByType}
          onChange={changeGroupBy}
        />
      );
    });

  return (
    <>
      {loading && <LoadingIndicator />}
      <Modal
        title={i18n.t('downloadReportPanel.labels.downloadOutdoorReport')}
        dismiss={onClose}
        animation={false}
        className={styles.downloadReportPanel}
        fullScreen
        enforceFocus={false}
        primaryButton={{
          title: i18n.t('downloadReportPanel.buttons.download'),
          callback: isCustomFieldsTab ? downloadCustomFieldsReport : download,
          // 走期自訂報表:沒有任何選項時 reportType 是 ''，download() 的四條分支都不成立
          // —— 按鈕能按卻毫無反應。停用它，讓「沒東西可下載」看得出來。
          // 自訂欄位報表:一個都沒勾就沒有欄位可下載;分母沒勾則會讓後端產出壞公式。
          disabled: isCustomFieldsTab
            ? !canDownloadOutdoorCustomReport(selectFields)
            : !reportType
        }}
        secondaryButton={{
          title: i18n.t('common.buttons.cancel'),
          callback: onClose
        }}
      >
        <Tabs
          id='reportDownloadPanelTab'
          defaultActiveKey={OutdoorReportDownloadPanelTab.NORMAL}
          activeKey={activeTab}
          onSelect={(tab) => tab && setActiveTab(tab)}
        >
          <Tab
            eventKey={OutdoorReportDownloadPanelTab.NORMAL}
            title={i18n.t('downloadReportPanel.labels.normal')}
          >
            <div className={styles.content}>
              <DateRangePickerField
                label={i18n.t('downloadReportPanel.labels.dateRange')}
                name='dateRange'
                popOverClassName={styles.datePicker}
                onChange={handleDateRangeChange}
                startDate={startDate}
                endDate={endDate}
                minDate={order.startDate}
                maxDate={order.endDate}
                format={'YYYY-MM-DD'}
                showTimePicker={false}
              />
              <SelectField
                label={i18n.t('downloadReportPanel.labels.reportType')}
                options={reportTypeOptions}
                name='reportOptions'
                simpleValue
                onChange={onSelectReportType}
                placeholder={i18n.t(
                  'DownloadReportPanel.placeholders.reportType'
                )}
                maxMenuHeight={150}
                value={reportType}
              />
            </div>
          </Tab>
          {customFieldsEnabled && (
            <Tab
              eventKey={OutdoorReportDownloadPanelTab.CUSTOM_FIELDS}
              title={i18n.t('downloadReportPanel.labels.advance')}
            >
              <div className={styles.content}>
                <DateRangePickerField
                  label={i18n.t('downloadReportPanel.labels.dateRange')}
                  name='customFieldsDateRange'
                  popOverClassName={styles.datePicker}
                  onChange={handleDateRangeChange}
                  startDate={startDate}
                  endDate={endDate}
                  minDate={order.startDate}
                  maxDate={order.endDate}
                  format={'YYYY-MM-DD'}
                  showTimePicker={false}
                />
                <CustomField
                  name='groupBy'
                  label={i18n.t('downloadReportPanel.labels.groupBy')}
                >
                  <div className={styles.groupBy}>{renderGroupBy()}</div>
                </CustomField>
                <CustomField
                  label={i18n.t('downloadReportPanel.labels.dataContent')}
                  formGroupClassName={styles.dataContent}
                />
                <CustomField
                  name='quickSelect'
                  label={i18n.t('downloadReportPanel.labels.quickSelect')}
                >
                  <div>
                    <div className={styles.quickSelect}>
                      <div
                        onClick={_.partial(
                          onQuickSelect,
                          OutdoorQuickSelect.CPM
                        )}
                      >
                        {i18n.t('downloadReportPanel.labels.cpmOption')}
                      </div>
                      <div
                        onClick={_.partial(
                          onQuickSelect,
                          OutdoorQuickSelect.ACPM
                        )}
                      >
                        {i18n.t('downloadReportPanel.labels.acpmOption')}
                      </div>
                      <div
                        onClick={_.partial(
                          onQuickSelect,
                          OutdoorQuickSelect.RESET
                        )}
                      >
                        {i18n.t('downloadReportPanel.labels.reset')}
                      </div>
                    </div>
                    {OUTDOOR_DATA_CONTENT_ROWS.map((row) =>
                      renderFieldChecks(row)
                    )}
                    <div className={styles.errorContainer}>
                      {Object.keys(fieldErrors).map((field) => (
                        <div key={field} className={styles.errorHint}>
                          {fieldErrors[field]}
                        </div>
                      ))}
                    </div>
                  </div>
                </CustomField>
                <CustomField
                  name='videoAdViewObjective'
                  label={i18n.t(
                    'downloadReportPanel.labels.videoAdViewObjective'
                  )}
                >
                  {renderFieldChecks(OUTDOOR_VIDEO_FIELDS)}
                </CustomField>
              </div>
            </Tab>
          )}
        </Tabs>
      </Modal>
    </>
  );
};
